import { Briefcase, MapPin, Clock, Users, Heart, Lightbulb } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import Layout from "@/components/Layout";

const Careers = () => {
  const openPositions = [
    {
      title: "Senior Training Consultant",
      department: "Training & Development",
      location: "Nairobi, Kenya",
      type: "Full-time",
      description: "Lead delivery of leadership development programs and organizational transformation initiatives.",
      requirements: [
        "Master's degree in relevant field",
        "5+ years training experience",
        "Certification in adult learning",
        "Excellent communication skills"
      ]
    },
    {
      title: "Project Management Trainer",
      department: "Professional Development",
      location: "Remote/Hybrid",
      type: "Contract",
      description: "Deliver project management training programs and certification courses.",
      requirements: [
        "PMP or equivalent certification",
        "3+ years project management experience",
        "Training or facilitation background",
        "Technology proficiency"
      ]
    },
    {
      title: "Business Development Manager",
      department: "Sales & Marketing",
      location: "Nairobi, Kenya",
      type: "Full-time",
      description: "Drive business growth through client acquisition and partnership development.",
      requirements: [
        "Bachelor's degree in Business",
        "3+ years B2B sales experience",
        "Strong networking abilities",
        "Results-driven mindset"
      ]
    }
  ];

  const benefits = [
    {
      icon: Heart,
      title: "Health & Wellness",
      description: "Comprehensive health insurance and wellness programs"
    },
    {
      icon: Lightbulb,
      title: "Learning & Development",
      description: "Continuous professional development and training opportunities"
    },
    {
      icon: Users,
      title: "Collaborative Culture",
      description: "Work with passionate professionals in a supportive environment"
    },
    {
      icon: Clock,
      title: "Work-Life Balance",
      description: "Flexible working arrangements and time-off policies"
    }
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="py-16 lg:py-20 bg-gradient-secondary" id="careers">
        <div className="container mx-auto px-4 text-center space-y-6">
          <h1 className="text-4xl lg:text-5xl font-bold text-foreground">
            Join Our Team
          </h1>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Be part of Africa's leading capacity building organization. Help us transform 
            individuals and organizations across the continent.
          </p>
        </div>
      </section>

      {/* Company Culture */}
      <section className="py-16 lg:py-20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h2 className="text-3xl lg:text-4xl font-bold text-foreground">
                Why Work With Us?
              </h2>
              <p className="text-lg text-muted-foreground">
                At TCLA-X, we believe in empowering our team members to excel in their careers 
                while making a meaningful impact across Africa. Join a culture that values 
                innovation, growth, and transformation.
              </p>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-primary rounded-full"></div>
                  <span className="text-muted-foreground">Make a real impact on African leadership development</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-primary rounded-full"></div>
                  <span className="text-muted-foreground">Work with industry-leading professionals</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-primary rounded-full"></div>
                  <span className="text-muted-foreground">Continuous learning and professional growth</span>
                </div>
              </div>
            </div>
            <div className="bg-gradient-primary rounded-2xl p-8 text-white">
              <div className="space-y-6">
                <h3 className="text-2xl font-bold">Our Mission</h3>
                <p className="text-white/90">
                  We're committed to transforming Africa through world-class capacity building. 
                  Every team member plays a crucial role in empowering leaders and organizations 
                  to achieve excellence.
                </p>
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold">500+</div>
                    <div className="text-xs text-white/80">Organizations Served</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold">15+</div>
                    <div className="text-xs text-white/80">Countries Reached</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 lg:py-20 bg-gradient-secondary">
        <div className="container mx-auto px-4">
          <div className="text-center space-y-4 mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground">
              Benefits & Perks
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              We offer competitive benefits and a supportive work environment that helps you thrive.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <Card key={index} className="text-center shadow-subtle">
                <CardContent className="p-6 space-y-4">
                  <div className="w-12 h-12 bg-gradient-primary rounded-full flex items-center justify-center mx-auto">
                    <benefit.icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-semibold text-foreground">{benefit.title}</h3>
                  <p className="text-sm text-muted-foreground">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section className="py-16 lg:py-20">
        <div className="container mx-auto px-4">
          <div className="text-center space-y-4 mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground">
              Open Positions
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Explore current opportunities to join our growing team.
            </p>
          </div>

          <div className="space-y-6">
            {openPositions.map((position, index) => (
              <Card key={index} className="shadow-subtle hover:shadow-elegant transition-smooth">
                <CardHeader>
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
                    <div className="space-y-2">
                      <CardTitle className="text-xl">{position.title}</CardTitle>
                      <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                        <div className="flex items-center">
                          <Briefcase className="w-4 h-4 mr-1" />
                          {position.department}
                        </div>
                        <div className="flex items-center">
                          <MapPin className="w-4 h-4 mr-1" />
                          {position.location}
                        </div>
                        <div className="flex items-center">
                          <Clock className="w-4 h-4 mr-1" />
                          {position.type}
                        </div>
                      </div>
                    </div>
                    <Button>Apply Now</Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-muted-foreground">{position.description}</p>
                  <div>
                    <h4 className="font-semibold text-foreground mb-2">Key Requirements:</h4>
                    <ul className="space-y-1">
                      {position.requirements.map((req, reqIndex) => (
                        <li key={reqIndex} className="text-sm text-muted-foreground flex items-start">
                          <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2 mr-2 flex-shrink-0"></div>
                          {req}
                        </li>
                      ))}
                    </ul>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Application Process */}
      <section className="py-16 lg:py-20 bg-gradient-primary text-white">
        <div className="container mx-auto px-4 text-center space-y-8">
          <div className="space-y-4">
            <h2 className="text-3xl lg:text-4xl font-bold">
              Ready to Join Us?
            </h2>
            <p className="text-lg text-white/90 max-w-2xl mx-auto">
              Send your application to our HR team and take the first step towards 
              a rewarding career with TCLA-X.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button variant="secondary" size="lg">
              careers@tcla-x.com
            </Button>
            <Button variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-primary">
              View All Opportunities
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Careers;